/****************************************************************************************************
*                                                                                                   *
*   Freshwater v3 FRESHWATER JS FILE                                                                *
*                                                                                                   *
*   --------------------------------                                                                *
*   ¡    DO NOT EDIT THIS FILE     !                                                                *
*   --------------------------------                                                                *
*                                                                                                   *
*   (use /assets/1-client.js.liquid for client specific styling)                                    *
*                                                                                                   *
****************************************************************************************************/

// ============================================================================
// FRESHWATER DEBUG UTILITY
// ============================================================================

/**
 * Freshwater debug utility for consistent console logging.
 * Only logs when debug_mode=true is passed as a URL parameter.
 */
window.Freshwater = {
    DEBUG_MODE: false,
    console: function(type, ...messages) {
        if (!this.DEBUG_MODE) return;
        
        const style = "background-color: #e0e8ff; color: #2a52c9; font-weight: bold; margin-bottom: 2px; padding: 2px;";
        
        if (["log", "warn", "error"].includes(type)) {
            console[type]("🔹 %cFreshwater", style, ...messages);
        }
    },
    
    /**
     * Debug utility to check Tiny-Slider carousel containers for common issues.
     * Run in console: Freshwater.checkCarousels()
     * 
     * Checks for:
     * - Containers that are HTML elements (should be UL)
     * - Parents that are document or documentElement
     * - Missing children
     * - Accidental .fresh-carousel classes on <html>
     */
    checkCarousels: function() {
        // Use Freshwater console utility (respects DEBUG_MODE)
        var logger = window.Freshwater && typeof window.Freshwater.console === 'function'
            ? window.Freshwater.console.bind(window.Freshwater)
            : function(type, ...msgs) { 
                // Fallback to console if Freshwater.console not available
                if (console[type]) console[type](...msgs);
            };
        
        logger('log', '🔍 Checking Tiny-Slider carousel containers...');
        logger('log', '');
        
        var carousels = document.querySelectorAll('.fresh-carousel[data-slider-config]');
        logger('log', 'Found', carousels.length, 'carousel(s) with data-slider-config');
        logger('log', '');
        
        var issues = [];
        
        carousels.forEach(function(carousel, i) {
            logger('log', 'Carousel', i + ':', {
                tagName: carousel.tagName,
                id: carousel.id || '(no id)',
                classes: carousel.className,
                parentNode: carousel.parentNode ? carousel.parentNode.nodeName : 'null',
                parentNodeType: carousel.parentNode ? carousel.parentNode.nodeType : 'null',
                inBody: document.body.contains(carousel),
                childrenCount: carousel.children.length,
                ownerDocument: carousel.ownerDocument === document
            });
            
            // Check for issues
            if (carousel.tagName === 'HTML') {
                issues.push('Carousel ' + i + ': Container is <html> element (should be <ul>)');
            }
            if (carousel.parentNode === document || carousel.parentNode === document.documentElement) {
                issues.push('Carousel ' + i + ': Parent is document or documentElement');
            }
            if (carousel.parentNode && carousel.parentNode.nodeType !== 1) {
                issues.push('Carousel ' + i + ': Parent nodeType is not 1 (Element)');
            }
            if (carousel.children.length < 1) {
                issues.push('Carousel ' + i + ': Has no children');
            }
            if (!document.body.contains(carousel)) {
                issues.push('Carousel ' + i + ': Not in document.body');
            }
        });
        
        // Check for accidental .fresh-carousel on <html>
        var htmlWithClass = document.querySelector('html.fresh-carousel, html.fresh-carousel--columns-md-3, html.fresh-carousel--columns-sm-1');
        if (htmlWithClass) {
            issues.push('CRITICAL: <html> element has .fresh-carousel class');
        }
        
        logger('log', '');
        logger('log', issues.length > 0 ? '❌ Issues found:' : '✅ No issues found');
        issues.forEach(function(issue) {
            logger('warn', issue);
        });
        
        return {
            carousels: carousels.length,
            issues: issues.length,
            details: issues
        };
    }
};

// Check for debug_mode param in URL
(function() {
    const params = new URLSearchParams(window.location.search);
    if (params.get("debug_mode") === "true") {
        window.Freshwater.DEBUG_MODE = true;
    }
})();

// ============================================================================
// GLOBAL VARIABLES
// ============================================================================

// Freshwater-specific global variables (all prefixed with 'fresh' to avoid conflicts)
// Note: Using 'var' for globals to maintain ES5 compatibility and avoid block scoping issues
var freshWindowResizeTimeout = null;
var freshCurrentHostname = window.location.hostname;

// External library instances (no prefix needed - these are third-party)
// Defensive: Check if LazyLoad is available before initializing
var lazyLoadInstance = null;
if (typeof LazyLoad !== 'undefined') {
    lazyLoadInstance = new LazyLoad({});
} else if (window.Freshwater) {
    window.Freshwater.console('warn', 'LazyLoad is not available; skipping lazyLoadInstance init');
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Waits for an element to appear in the DOM before resolving.
 * Uses MutationObserver to watch for element creation.
 * 
 * @param {string} selector - CSS selector for the element to wait for
 * @returns {Promise<Element>} Promise that resolves when element is found
 * 
 * Reference: https://medium.com/@ryan_forrester_/javascript-wait-for-element-to-exist-simple-explanation-1cd8c569e354
 */
function freshWaitForElement(selector) {
    // Performance: Check if element already exists before setting up observer
    var existingElement = document.querySelector(selector);
    if (existingElement) {
        return Promise.resolve(existingElement);
    }
    
    return new Promise((resolve) => {
        const observer = new MutationObserver(function() {
            const element = document.querySelector(selector);
            if (element) {
                observer.disconnect();
                resolve(element);
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    });
}

// ============================================================================
// INITIALIZATION FUNCTIONS
// ============================================================================


/**
 * Waits for Tiny-Slider library to be available.
 * Polls for tns function to be available, useful for theme editor where scripts load asynchronously.
 * 
 * @param {number} maxAttempts - Maximum number of attempts (default: 100)
 * @param {number} interval - Interval between attempts in ms (default: 50)
 * @returns {Promise<boolean>} Promise that resolves to true if Tiny-Slider is available, false otherwise
 */
function freshWaitForTinySlider(maxAttempts, interval) {
    maxAttempts = maxAttempts || 100;
    interval = interval || 50;
    
    // If already available, return immediately
    if (typeof tns === 'function') {
        return Promise.resolve(true);
    }
    
    return new Promise(function(resolve) {
        var attempts = 0;
        var checkInterval = setInterval(function() {
            attempts++;
            if (typeof tns === 'function') {
                clearInterval(checkInterval);
                if (window.Freshwater) {
                    window.Freshwater.console('log', 'Tiny-Slider available after', attempts, 'attempts');
                }
                resolve(true);
            } else if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
                if (window.Freshwater) {
                    window.Freshwater.console('warn', 'Tiny-Slider not available after', maxAttempts, 'attempts');
                }
                resolve(false);
            }
        }, interval);
    });
}

/**
 * Initialize Freshwater carousels using Tiny-Slider.
 * 
 * - Reads options from data-slider-config on .fresh-carousel elements.
 * - Respects breakpoint disable classes:
 *      .fresh-carousel--disabled--sm
 *      .fresh-carousel--disabled--md
 * - Initializes each carousel only once.
 * - Skips carousels already wrapped by Tiny-Slider (.tns-outer).
 * 
 * Uses vanilla JavaScript - no jQuery dependency.
 * 
 * @param {boolean} waitForLibrary - If true, waits for Tiny-Slider to be available before initializing (default: false)
 * @returns {void|Promise<void>} Returns Promise if waitForLibrary is true, void otherwise
 */
function freshInitCarouselsTiny(waitForLibrary) {
    // If waitForLibrary is true, wait for Tiny-Slider before proceeding
    if (waitForLibrary) {
        // First check if there are any carousels to initialize
        var carousels = document.querySelectorAll('.fresh-carousel[data-slider-config]');
        if (carousels.length === 0) {
            if (window.Freshwater) {
                window.Freshwater.console('log', 'No carousels found, skipping Tiny-Slider wait');
            }
            return Promise.resolve();
        }
        
        if (window.Freshwater) {
            window.Freshwater.console('log', 'Waiting for Tiny-Slider, found', carousels.length, 'carousel(s)');
        }
        
        return freshWaitForTinySlider().then(function(available) {
            if (available) {
                if (window.Freshwater) {
                    window.Freshwater.console('log', 'Tiny-Slider available, initializing carousels');
                }
                freshInitCarouselsTiny(false);
            } else {
                if (window.Freshwater) {
                    window.Freshwater.console('warn', 'Tiny-Slider not available, carousels will not initialize');
                }
            }
        });
    }
    
    if (typeof tns !== 'function') {
        if (window.Freshwater) {
            window.Freshwater.console('warn', 'tiny-slider not available; skipping init');
        }
        return;
    }

    var isDesktop = window.innerWidth >= 768;
    var carousels = document.querySelectorAll('.fresh-carousel[data-slider-config]');

    if (window.Freshwater) {
        window.Freshwater.console('log', 'Tiny-Slider is available, initializing carousels...', 'Found', carousels.length, 'carousel(s)');
    }

    carousels.forEach(function (carousel, index) {
        if (window.Freshwater) {
            window.Freshwater.console('log', 'Processing carousel', index, carousel);
        }

        // Skip if already wrapped by Tiny-Slider
        if (carousel.closest('.tns-outer')) {
            return;
        }
        
        // Skip if already initialized and active
        if (carousel._freshSliderInstance) {
            var instance = carousel._freshSliderInstance;
            if (typeof instance.destroy === 'function' && instance.isOn && instance.isOn()) {
                return;
            }
        }

        // Respect "disable on breakpoint" classes
        var shouldDisable = false;
        if (isDesktop && carousel.classList.contains('fresh-carousel--disabled--md')) {
            shouldDisable = true;
        } else if (!isDesktop && carousel.classList.contains('fresh-carousel--disabled--sm')) {
            shouldDisable = true;
        }

        if (shouldDisable) {
            return;
        }

        // Validate carousel is in DOM with valid parent
        if (!carousel.parentNode || 
            carousel.parentNode === document || 
            carousel.parentNode === document.documentElement ||
            !document.body.contains(carousel)) {
            return;
        }

        // Parse config from data-slider-config
        var configStr = carousel.getAttribute('data-slider-config') || '{}';
        var options;

        try {
            options = JSON.parse(configStr);
        } catch (err) {
            if (window.Freshwater) {
                window.Freshwater.console('error', 'Invalid JSON in data-slider-config', err);
            }
            return;
        }

        // Use the actual element as container
        options.container = carousel;

        // Set defaults
        if (typeof options.slideBy === 'undefined') options.slideBy = 1;
        if (typeof options.speed === 'undefined') options.speed = 400;
        if (typeof options.axis === 'undefined') options.axis = 'horizontal';
        if (typeof options.loop === 'undefined') options.loop = true;
        if (typeof options.mouseDrag === 'undefined') options.mouseDrag = true;
        if (typeof options.autoplayButtonOutput === 'undefined') options.autoplayButtonOutput = false;
        
        // Force start at first slide (index 0) to ensure we start at slide 1
        options.startIndex = 0;
        
        // Disable center mode when looping and starting at index 0
        // Center mode combined with looping causes Tiny-Slider to show last slide first
        // (e.g., showing 4,1,2 or 5,1,2 instead of 1,2,3)
        if (options.loop && options.startIndex === 0) {
            options.center = false;
        }

        // Store reference to carousel's parentNode before Tiny-Slider manipulates DOM
        // This is critical for the monkey-patch to work correctly
        var originalParentNode = carousel.parentNode;
        
        // Use setTimeout to ensure DOM is fully ready and avoid timing issues
        setTimeout(function() {
            // CRITICAL: Monkey-patch Document.appendChild and Node.appendChild
            // Tiny-Slider has a bug where it tries to appendChild directly to document,
            // which throws "Only one element on document allowed." This patch redirects
            // those calls to the carousel's original parentNode.
            var originalDocAppend = Document.prototype.appendChild;
            var originalNodeAppend = Node.prototype.appendChild;

            Document.prototype.appendChild = function(node) {
                if (this === document && node && node.nodeType === 1) {
                    if (originalParentNode && originalParentNode !== document && originalParentNode !== document.documentElement) {
                        return originalParentNode.appendChild(node);
                    }
                    return document.documentElement.appendChild(node);
                }
                return originalDocAppend.call(this, node);
            };
            
            Node.prototype.appendChild = function(node) {
                if (this === document && node && node.nodeType === 1) {
                    if (originalParentNode && originalParentNode !== document && originalParentNode !== document.documentElement) {
                        return originalParentNode.appendChild(node);
                    }
                    return document.documentElement.appendChild(node);
                }
                return originalNodeAppend.call(this, node);
            };

            try {
                // Validate parent is still valid before init
                if (!carousel.parentNode || 
                    carousel.parentNode === document || 
                    carousel.parentNode === document.documentElement ||
                    !document.body.contains(carousel) ||
                    !originalParentNode || 
                    originalParentNode === document || 
                    originalParentNode === document.documentElement) {
                    return;
                }
                
                // Final check: if already wrapped by Tiny-Slider, skip
                if (carousel.closest('.tns-outer')) {
                    return;
                }
                
                // Destroy existing instance if present
                if (carousel._freshSliderInstance && typeof carousel._freshSliderInstance.destroy === 'function') {
                    try {
                        carousel._freshSliderInstance.destroy();
                    } catch (destroyErr) {
                        // Silently fail - instance may already be destroyed
                    }
                    carousel._freshSliderInstance = null;
                }

                // Initialize Tiny-Slider
                var slider = tns(options);
                carousel._freshSliderInstance = slider;
            } catch (innerErr) {
                if (window.Freshwater) {
                    window.Freshwater.console('error', 'Tiny-Slider init failed', innerErr);
                }
            } finally {
                // Always restore original appendChild methods
                Document.prototype.appendChild = originalDocAppend;
                Node.prototype.appendChild = originalNodeAppend;
            }
        }, 0);
    });
}

/**
 * Global initialization function called on DOMContentLoaded.
 * Initializes smooth scrolling, external links, functionality buttons, video sound controls, 
 * video poster image conversion, Tiny-Slider carousels, and Shopify section event listeners.
 * 
 * Uses vanilla JavaScript - no jQuery dependency.
 * 
 * @param {Event} e - DOMContentLoaded event
 */
const freshGlobalInit = function (e) {

    // Initialize smooth scrolling for anchor links
    freshSmoothScrollToInlineAnchors();

    // Initialize external links to open in new window
    freshOpenExternalLinksInNewWindow();

    // Initialize functionality buttons (add to cart, Klaviyo, smooth scroll)
    freshAddFunctionalityButtons();

    // Initialize cart drawer subscription upsell handler
    freshInitCartDrawerSubscriptionUpsell();
    // Prevent cart change handlers from firing on subscription select changes
    freshInitCartDrawerSubscriptionSelectGuard();

    // Create video sound buttons for videos that don't have them
    freshCreateVideoSoundButtons();

    // Initialize video sound toggle functionality
    freshVideoSoundListener();

    // Convert video poster images to high quality
    freshConvertVideoPosterImages();

    // Initialize Tiny-Slider carousels (params passed via the data-slider-config attribute)
    // Tiny-Slider is loaded with defer, so it's guaranteed to be ready before DOMContentLoaded
    freshInitCarouselsTiny();

    // Performance: Shared handler for Shopify section/block events to reduce duplication
    function handleShopifySectionEvent(e) {
        // Defensive: Only update LazyLoad if it's available
        if (lazyLoadInstance && typeof lazyLoadInstance.update === 'function') {
            lazyLoadInstance.update();
        }
        
        // Create video sound buttons for dynamically added videos
        freshCreateVideoSoundButtons();
        // Convert video poster images for dynamically added content
        freshConvertVideoPosterImages();
        
        // Re-init carousels for dynamically added/edited sections/blocks
        // Check if event target is actually a Shopify section/block to avoid
        // re-initing when Tiny-Slider manipulates DOM during transitions
        var target = e && e.target ? e.target : null;
        
        // Skip if event appears to be from Tiny-Slider DOM manipulation
        if (target && target.closest && target.closest('.tns-outer')) {
            return;
        }
        
        // Only re-init if this looks like a real Shopify section event
        var isShopifySection = target && (
            (target.classList && target.classList.contains('shopify-section')) ||
            (target.closest && target.closest('.shopify-section')) ||
            (target.hasAttribute && target.hasAttribute('data-section-id'))
        );
        
        if (!isShopifySection && target) {
            return;
        }
        
        // Re-init carousels for new sections/blocks
        // In theme editor, we need to wait for:
        // 1. DOM to be fully updated (small delay)
        // 2. Tiny-Slider library to be available
        // Use requestAnimationFrame to ensure DOM is painted, then wait for Tiny-Slider
        var isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode;
        var delay = isDesignMode ? 200 : 50; // Longer delay in design mode
        
        requestAnimationFrame(function() {
            setTimeout(function() {
                freshInitCarouselsTiny(true).catch(function(err) {
                    if (window.Freshwater) {
                        window.Freshwater.console('error', 'Error initializing carousels:', err);
                    }
                });
            }, delay);
        });
    }

    // Shopify section load listener (vanilla JS event delegation)
    document.addEventListener('shopify:section:load', handleShopifySectionEvent);
    
    // Shopify section reorder listener (vanilla JS event delegation)
    document.addEventListener('shopify:section:reorder', handleShopifySectionEvent);

    // Shopify block load listener (vanilla JS event delegation)
    document.addEventListener('shopify:block:load', handleShopifySectionEvent);
};

/**
 * Window resize handler with debouncing.
 * 
 * Note: Tiny-Slider handles responsive behavior internally via its responsive option,
 * so we do NOT re-initialize carousels on resize. Re-initializing can cause internal
 * state issues and trigger the HierarchyRequestError bug.
 * 
 * @param {Event} e - Resize event
 */
const freshGlobalResize = function (e) {
    // Tiny-Slider handles responsive behavior internally - no re-init needed
    // Any other resize-specific logic can be added here
};

/**
 * Converts video poster images to high quality by removing _small and _medium suffixes.
 * Strips the _small and _medium image path names from video poster attributes to ensure 
 * higher quality video thumbnails are displayed.
 * 
 * Works with dynamically added content - can be called multiple times safely.
 * 
 * @author Alexander Khost
 * Reference: https://community.shopify.com/c/shopify-design/why-are-my-video-thumbnails-blurry-on-the-dawn-theme/m-p/2291713
 */
function freshConvertVideoPosterImages() {
    const videos = document.querySelectorAll('.fresh_video');
    
    videos.forEach(function(video) {
        const poster = video.getAttribute('poster');
        
        // Skip if no poster attribute
        if (!poster) {
            return;
        }
        
        // Replace _small or _medium with empty string to get high quality version
        // Check _small first, then _medium (in case both exist, though unlikely)
        let newPoster = poster;
        if (newPoster.includes('_small')) {
            newPoster = newPoster.replace('_small', '');
        } else if (newPoster.includes('_medium')) {
            newPoster = newPoster.replace('_medium', '');
        }
        
        // Only update if the poster actually changed (performance optimization)
        if (newPoster !== poster) {
            video.setAttribute('poster', newPoster);
        }
    });
}

/**
 * Implements smooth scrolling for internal anchor links.
 * Enhances user experience by creating a seamless and visually pleasing navigation 
 * experience when users click on internal anchor links within the page.
 * 
 * Uses event delegation for better performance and compatibility with dynamic content.
 * Respects user's motion preferences for accessibility (WCAG 2.2 compliance).
 * Uses vanilla JavaScript for better performance and no jQuery dependency.
 * 
 * @author Alexander Khost
 */
function freshSmoothScrollToInlineAnchors() {
    /**
     * Smoothly scrolls to a target element.
     * @param {Element} targetElement - The element to scroll to
     * @param {number} duration - Scroll duration in milliseconds (0 for instant)
     */
    function smoothScrollTo(targetElement, duration) {
        if (duration <= 0) {
            // Instant scroll for reduced motion
            targetElement.scrollIntoView({ behavior: 'auto', block: 'start' });
            return;
        }
        
        var startPosition = window.pageYOffset || document.documentElement.scrollTop;
        var targetPosition = targetElement.getBoundingClientRect().top + startPosition;
        var distance = targetPosition - startPosition;
        var startTime = null;
        
        function animation(currentTime) {
            if (startTime === null) {
                startTime = currentTime;
            }
            
            var timeElapsed = currentTime - startTime;
            var progress = Math.min(timeElapsed / duration, 1);
            
            // Easing function (ease-in-out)
            var ease = progress < 0.5 
                ? 2 * progress * progress 
                : 1 - Math.pow(-2 * progress + 2, 2) / 2;
            
            window.scrollTo(0, startPosition + distance * ease);
            
            if (timeElapsed < duration) {
                requestAnimationFrame(animation);
            } else {
                // Ensure we end exactly at the target position
                window.scrollTo(0, targetPosition);
            }
        }
        
        requestAnimationFrame(animation);
    }
    
    // Use event delegation on document for better performance and dynamic content support
    document.addEventListener('click', function(event) {
        var link = event.target.closest('a[href^="#"]');
        
        // Skip if not an anchor link
        if (!link) {
            return;
        }
        
        var href = link.getAttribute('href');
        
        // Skip if href is just "#" or empty
        if (!href || href === '#' || href.length <= 1) {
            return;
        }
        
        // Get target element
        var targetId = href.substring(1); // Remove the #
        var targetElement = document.getElementById(targetId) || document.querySelector('[name="' + targetId + '"]');
        
        // If target doesn't exist, allow default behavior (browser will handle it)
        if (!targetElement) {
            return;
        }
        
        // Prevent default anchor jump
        event.preventDefault();
        event.stopPropagation();
        
        // Check for reduced motion preference (accessibility - WCAG 2.2)
        var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var scrollDuration = prefersReducedMotion ? 0 : 500;
        
        // Smooth scroll to target
        smoothScrollTo(targetElement, scrollDuration);
    });
}

/**
 * Opens all external links within 'body' in a new window using event delegation.
 * Enhances user experience by allowing users to stay on the current page while viewing 
 * external link content in a new window. Works with dynamically added content.
 * 
 * Uses event delegation for better performance and compatibility with dynamic content.
 * Adds rel="noopener noreferrer" for security and accessibility best practices.
 * Uses vanilla JavaScript for better performance and no jQuery dependency.
 * 
 * @param {string} [filter] - Optional CSS selector to filter targeted external links. 
 *                            Defaults to excluding links with classes: .jdgm-paginate__page, .jdgm-star, .jdgm-paginate__load-more
 *                            to filter out review links.
 * 
 * Reference: https://css-tricks.com/snippets/jquery/open-external-links-in-new-window/
 */
function freshOpenExternalLinksInNewWindow(filter) {
    // Classes to exclude (default filter behavior)
    var excludedClasses = ['jdgm-paginate__page', 'jdgm-star', 'jdgm-paginate__load-more'];
    
    // Use event delegation on document for better performance and dynamic content support
    document.addEventListener('click', function(event) {
        var link = event.target.closest('a');
        
        // Skip if not a link
        if (!link) {
            return;
        }
        
        // Check if link matches the filter (if provided)
        // The filter determines which links should be processed
        if (filter && typeof filter === 'string') {
            // Try to use matches() for custom filter (works for simple selectors)
            try {
                if (link.matches && !link.matches(filter)) {
                    // Link doesn't match the filter, skip it
                    return;
                }
            } catch (e) {
                // If matches() fails (complex selector like :not()), fall back to default behavior
                // Complex selectors require a selector library or manual parsing
                window.Freshwater.console('warn', 'Complex selector filter not fully supported, using default exclusion');
            }
        }
        
        // Default exclusion check (for default filter behavior: exclude review links)
        // This mimics the original 'a:not(.jdgm-paginate__page,.jdgm-star,.jdgm-paginate__load-more)' behavior
        for (var i = 0; i < excludedClasses.length; i++) {
            if (link.classList.contains(excludedClasses[i])) {
                return; // Skip excluded links
            }
        }
        
        var href = link.href;
        
        // Skip if no href or if it's a hash/anchor link
        if (!href || href.charAt(0) === '#') {
            return;
        }
        
        // Check if link is external by comparing hostnames
        try {
            var linkHost = new URL(href).hostname;
            
            // If it's an external link (use cached hostname)
            if (linkHost !== freshCurrentHostname) {
                // Only intercept unmodified left-clicks to preserve native behavior
                // (Ctrl/Cmd+click, middle-click, etc. should work normally)
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                    return;
                }
                
                // Add security attributes if not already present
                var relAttr = link.getAttribute('rel') || '';
                if (!relAttr.includes('noopener')) {
                    link.setAttribute('rel', (relAttr + ' noopener noreferrer').trim());
                } else if (!relAttr.includes('noreferrer')) {
                    link.setAttribute('rel', (relAttr + ' noreferrer').trim());
                }
                
                // Prevent default and open in new window
                event.preventDefault();
                event.stopPropagation();
                
                // Open in new window (security handled via rel attribute above)
                window.open(href, '_blank');
            }
        } catch (e) {
            // If URL parsing fails, skip this link
            return;
        }
    });
}

/**
 * Inline SVG icons for mute/unmute (theme does not ship icon-sound.webp / icon-sound-off.webp; img tags 404).
 */
var FRESH_VIDEO_SOUND_SVG_OFF = '<svg class="video-sound-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>';
var FRESH_VIDEO_SOUND_SVG_ON = '<svg class="video-sound-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>';

/**
 * Creates video sound buttons for videos that don't already have them.
 * Inserts a sound button after each video element that doesn't already have one.
 * 
 * @author Alexander Khost
 */
function freshCreateVideoSoundButtons() {
    // Find all video elements
    const videos = document.querySelectorAll('video');
    
    videos.forEach(function(video) {
        // Check if this video already has a sound button
        const nextSibling = video.nextElementSibling;
        const hasButton = nextSibling && nextSibling.classList.contains('video-sound-button');
        
        if (!hasButton) {
            // Create the button structure
            const buttonDiv = document.createElement('div');
            buttonDiv.className = 'video-sound-button';
            buttonDiv.setAttribute('role', 'button');
            buttonDiv.setAttribute('tabindex', '0');
            buttonDiv.setAttribute('aria-label', 'Unmute video');
            
            const span = document.createElement('span');
            span.innerHTML = FRESH_VIDEO_SOUND_SVG_OFF;
            
            buttonDiv.appendChild(span);
            
            // Insert after the video element
            if (video.parentNode) {
                video.parentNode.insertBefore(buttonDiv, video.nextSibling);
            }
        }
    });
}

/**
 * Video sound toggle listener.
 * Handles click events on video sound buttons to mute/unmute videos.
 * Ensures only one video plays sound at a time by muting all others when one is unmuted.
 * 
 * Uses event delegation for better performance and compatibility with dynamic content.
 * Uses vanilla JavaScript for better performance and no jQuery dependency.
 * 
 * Uses inline SVG (FRESH_VIDEO_SOUND_SVG_ON / OFF) — no theme image assets required.
 * 
 * @author Alexander Khost
 */
function freshVideoSoundListener() {
    // Use event delegation on document for better performance and dynamic content support
    document.addEventListener('click', function(event) {
        // Find the button wrapper (allows clicking anywhere on the button, not just the span)
        const buttonWrapper = event.target.closest('.video-sound-button');
        
        // Skip if not a video sound button
        if (!buttonWrapper) {
            return;
        }
        
        // Find the span element within the button (for image updates)
        const clickedElement = buttonWrapper.querySelector('span');
        if (!clickedElement) {
            return;
        }
        
        // Find the video element (previous sibling of the button wrapper)
        let videoElement = buttonWrapper.previousElementSibling;
        while (videoElement && videoElement.tagName !== 'VIDEO') {
            videoElement = videoElement.previousElementSibling;
        }
        
        // Skip if no video found
        if (!videoElement || videoElement.tagName !== 'VIDEO') {
            window.Freshwater.console('warn', 'Video element not found for sound button');
            return;
        }
        
        // Check if video is currently muted
        const isMuted = videoElement.muted;
        
        if (isMuted) {
            // Video is muted - unmute it
            // First, mute all videos on the page to ensure only one plays sound
            const allVideos = document.querySelectorAll('video');
            allVideos.forEach(function(video) {
                video.muted = true;
            });
            
            // Update all sound button icons to "off" (muted) state
            document.querySelectorAll('.video-sound-button span').forEach(function(span) {
                span.innerHTML = FRESH_VIDEO_SOUND_SVG_OFF;
            });
            document.querySelectorAll('.video-sound-button').forEach(function(btn) {
                btn.setAttribute('aria-label', 'Unmute video');
            });
            
            // Unmute this video
            videoElement.muted = false;
            
            // Update this button's icon to "on" state
            clickedElement.innerHTML = FRESH_VIDEO_SOUND_SVG_ON;
            buttonWrapper.setAttribute('aria-label', 'Mute video');
        } else {
            // Video is not muted - mute it
            videoElement.muted = true;
            
            clickedElement.innerHTML = FRESH_VIDEO_SOUND_SVG_OFF;
            buttonWrapper.setAttribute('aria-label', 'Unmute video');
        }
    });

    document.addEventListener('keydown', function(event) {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }
        const buttonWrapper = event.target.closest('.video-sound-button');
        if (!buttonWrapper) {
            return;
        }
        event.preventDefault();
        buttonWrapper.click();
    });
}

/**
 * Adds a product to the cart using Shopify's cart API.
 * 
 * @param {string|number} variantId - The product variant ID to add
 * @param {number} [quantity=1] - The quantity to add (default: 1)
 * @param {string|number} [sellingPlanId] - Optional selling plan ID for subscriptions
 * @returns {Promise} Promise that resolves with the cart response
 */
function freshAddProductToCart(variantId, quantity, sellingPlanId) {
    if (!variantId) {
        window.Freshwater.console('error', 'freshAddProductToCart: variantId is required');
        return Promise.reject(new Error('Variant ID is required'));
    }
    
    quantity = quantity || 1;
    
    // Build form data for cart add request
    const formData = new FormData();
    formData.append('id', variantId.toString());
    formData.append('quantity', quantity.toString());
    
    if (sellingPlanId) {
        formData.append('selling_plan', sellingPlanId.toString());
    }
    
    // If cart drawer exists, request its sections to update it properly
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        const sections = cartDrawer.getSectionsToRender();
        if (sections && sections.length > 0) {
            formData.append('sections', sections.map(function(section) {
                return section.id;
            }).join(','));
            formData.append('sections_url', window.location.pathname);
        }
    }
    
    // Use Shopify's cart add URL - must use .js extension for JSON API
    const cartAddUrl = window.routes && window.routes.cart_add_url 
        ? (window.routes.cart_add_url.endsWith('.js') ? window.routes.cart_add_url : window.routes.cart_add_url + '.js')
        : '/cart/add.js';
    
    // Set up fetch config similar to Dawn's product-form.js
    // This ensures Shopify returns JSON instead of HTML
    const fetchOptions = {
        method: 'POST',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
        },
        body: formData
    };
    
    // Don't set Content-Type - let browser set it automatically for FormData
    // This is important for FormData to work correctly
    // Accept header tells Shopify we want JSON response, not HTML
    
    return fetch(cartAddUrl, fetchOptions)
    .then(function(response) {
        // Try to parse as JSON - if it fails, it's likely HTML
        return response.json().catch(function(error) {
            // If JSON parsing fails, it's likely HTML
            return response.text().then(function(text) {
                window.Freshwater.console('error', 'Cart API returned non-JSON response:', text.substring(0, 200));
                return Promise.reject(new Error('Invalid response from cart API'));
            });
        });
    })
    .then(function(data) {
        if (data.status && data.status !== 200) {
            // Error adding to cart
            window.Freshwater.console('error', 'Error adding to cart:', data);
            if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish !== 'undefined') {
                // Publish cart error event if available (Dawn pattern)
                publish(PUB_SUB_EVENTS.cartError, {
                    source: 'fresh-button',
                    productVariantId: variantId,
                    errors: data.errors || data.description,
                    message: data.message
                });
            }
            return Promise.reject(data);
        }
        
        // Success - update cart drawer and trigger cart update event
        const cartDrawer = document.querySelector('cart-drawer');
        
        // If we requested sections and got them back, render the cart drawer
        if (cartDrawer && data.sections && typeof cartDrawer.renderContents === 'function') {
            // We got sections back - render them and open the drawer
            cartDrawer.renderContents(data);
            // Also remove is-empty class from cart-drawer element itself
            cartDrawer.classList.remove('is-empty');
        } else if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish !== 'undefined') {
            // No sections in response - use pub-sub system to update cart
            
            // Defensive: Handle both Promise-based and callback-based publish implementations
            function afterCartUpdate() {
                // After cart is updated, remove is-empty class and open the cart drawer
                // Use setTimeout to ensure DOM has updated after cart-drawer-items replacement
                setTimeout(function() {
                    if (cartDrawer) {
                        cartDrawer.classList.remove('is-empty');
                        const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
                        if (cartDrawerItems) {
                            cartDrawerItems.classList.remove('is-empty');
                        }
                        if (typeof cartDrawer.open === 'function') {
                            cartDrawer.open();
                        }
                    }
                }, 0);
            }
            
            // Publish cart update event (this notifies all cart components and updates cart-drawer-items)
            const publishResult = publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'fresh-button',
                productVariantId: variantId,
                cartData: data
            });
            
            // Handle both Promise-based and callback-based implementations
            if (publishResult && typeof publishResult.then === 'function') {
                publishResult.then(afterCartUpdate);
            } else {
                afterCartUpdate();
            }
        } else {
            // Fallback: if pub-sub system not available, manually update and open cart drawer
            if (cartDrawer) {
                // Manually trigger cart update for cart-drawer-items
                const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
                if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
                    // Defensive: Handle both Promise-based and callback-based onCartUpdate
                    function afterCartUpdate() {
                        // Remove is-empty class from both cart-drawer and cart-drawer-items
                        cartDrawer.classList.remove('is-empty');
                        const updatedCartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
                        if (updatedCartDrawerItems) {
                            updatedCartDrawerItems.classList.remove('is-empty');
                        }
                        if (typeof cartDrawer.open === 'function') {
                            cartDrawer.open();
                        }
                    }
                    
                    const updateResult = cartDrawerItems.onCartUpdate();
                    if (updateResult && typeof updateResult.then === 'function') {
                        updateResult.then(afterCartUpdate);
                    } else {
                        // If not a Promise, wait a bit for DOM to update
                        setTimeout(afterCartUpdate, 100);
                    }
                } else {
                    cartDrawer.classList.remove('is-empty');
                    if (typeof cartDrawer.open === 'function') {
                        cartDrawer.open();
                    }
                }
            }
        }
        
        return data;
    })
    .catch(function(error) {
        window.Freshwater.console('error', 'Error adding product to cart:', error);
        return Promise.reject(error);
    });
}

/**
 * Updates a cart line to a specific quantity.
 * 
 * @param {number} line - The cart line number (1-based)
 * @param {number} quantity - The new quantity for the line
 * @returns {Promise} Promise that resolves with the cart response
 */
function freshCartChangeLine(line, quantity) {
    if (!line && line !== 0) {
        if (window.Freshwater) {
            window.Freshwater.console('error', 'freshCartChangeLine: line is required');
        }
        return Promise.reject(new Error('Line is required'));
    }

    const changeUrl = window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change.js';
    const fetchOptions = typeof fetchConfig === 'function' ? fetchConfig() : {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    };

    fetchOptions.body = JSON.stringify({
        line: line,
        quantity: quantity
    });

    return fetch(changeUrl, fetchOptions)
    .then(function(response) {
        return response.json();
    })
    .then(function(data) {
        if (data.status && data.status !== 200) {
            return Promise.reject(data);
        }
        return data;
    })
    .catch(function(error) {
        if (window.Freshwater) {
            window.Freshwater.console('error', 'Error updating cart line:', error);
        }
        return Promise.reject(error);
    });
}

var freshCartDrawerUpsellInitialized = false;

/**
 * Initializes cart drawer subscription upsell click handler.
 */
function freshInitCartDrawerSubscriptionUpsell() {
    if (freshCartDrawerUpsellInitialized) {
        return;
    }

    freshCartDrawerUpsellInitialized = true;

    document.addEventListener('click', function(event) {
        const button = event.target && event.target.closest
            ? event.target.closest('.js-cart-subscribe-upsell')
            : null;

        if (!button) {
            return;
        }

        event.preventDefault();

        if (button.disabled) {
            return;
        }

        const line = parseInt(button.dataset.line, 10);
        const variantId = button.dataset.variantId;
        const quantity = parseInt(button.dataset.quantity, 10) || 1;
        const wrapper = button.closest('.cart-subscribe-upsell');
        const select = wrapper ? wrapper.querySelector('.js-cart-subscribe-upsell-select') : null;
        const sellingPlanId = select && select.value ? select.value : button.dataset.sellingPlanId;

        if (!line || !variantId || !sellingPlanId) {
            if (window.Freshwater) {
                window.Freshwater.console('error', 'Cart subscription upsell: Missing required data attributes');
            }
            return;
        }

        button.setAttribute('aria-busy', 'true');
        button.setAttribute('disabled', 'disabled');

        freshCartChangeLine(line, 0)
        .then(function() {
            return freshAddProductToCart(variantId, quantity, sellingPlanId);
        })
        .catch(function(error) {
            if (window.Freshwater) {
                window.Freshwater.console('error', 'Cart subscription upsell failed', error);
            }
        })
        .finally(function() {
            button.removeAttribute('aria-busy');
            button.removeAttribute('disabled');
        });
    });
}

/**
 * Prevents cart change handlers from treating subscription selects as quantity inputs.
 */
function freshInitCartDrawerSubscriptionSelectGuard() {
    document.addEventListener('change', function(event) {
        var target = event && event.target ? event.target : null;
        if (target && target.tagName === 'OPTION' && target.parentElement) {
            target = target.parentElement;
        }

        const select = target && target.matches
            ? target.matches('.js-cart-subscribe-upsell-select')
                ? target
                : null
            : null;

        if (!select) {
            return;
        }

        event.stopPropagation();
    }, true);
}

/**
 * Extracts a value from a class name following the pattern: prefix-value
 * Cleans the value by removing line breaks and non-ASCII characters.
 * 
 * @param {string} className - The class name to parse
 * @param {string} prefix - The prefix to look for (e.g., 'freshvariant')
 * @returns {string|null} The extracted value or null if not found
 */
function freshExtractClassValue(className, prefix) {
    if (!className || !prefix) {
        return null;
    }
    
    const parts = className.split('-');
    if (parts.length < 2 || parts[0] !== prefix) {
        return null;
    }
    
    // Get everything after the prefix (handles multi-part values)
    const value = className.substring(prefix.length + 1);
    
    if (!value || value === '') {
        return null;
    }
    
    // Clean the value: remove line breaks and non-ASCII characters
    return value.replace(/(?:\r\n|\r|\n)/g, '').replace(/[^\x00-\x7F]/g, '');
}

/**
 * Handles modal functionality for buttons with .fresh-modal-button class
 * Finds modal content by ID matching the button's href (e.g., #my-section)
 * 
 * @param {HTMLElement} button - The button element that triggered the modal
 * @returns {boolean} True if modal was handled, false otherwise
 */
function freshHandleModal(button) {
    const href = button.getAttribute('href');
    
    // Check if href is an internal link (starts with #)
    if (!href || !href.startsWith('#')) {
        return false;
    }
    
    // Extract the ID (remove the #)
    const modalId = href.substring(1);
    
    // Find the modal element by ID
    const modalElement = document.getElementById(modalId);
    
    if (!modalElement) {
        window.Freshwater.console('warn', 'Freshwater Modal: Element with ID "' + modalId + '" not found');
        return false;
    }
    
    // Check if element has fresh-modal class
    if (!modalElement.classList.contains('fresh-modal')) {
        window.Freshwater.console('warn', 'Freshwater Modal: Element with ID "' + modalId + '" does not have .fresh-modal class');
        return false;
    }
    
    // Open the modal
    freshOpenModal(modalElement);
    
    return true;
}

/**
 * Opens a modal
 * 
 * @param {HTMLElement} modalElement - The modal element to open
 */
function freshOpenModal(modalElement) {
    // Prevent body scroll
    document.body.classList.add('fresh-modal-open');
    
    // Show modal
    modalElement.classList.add('fresh-modal--active');
    
    // Focus management for accessibility
    const closeButton = modalElement.querySelector('.fresh-modal__close');
    if (closeButton) {
        closeButton.focus();
    }
}

/**
 * Closes a modal
 * 
 * @param {HTMLElement} modalElement - The modal element to close
 */
function freshCloseModal(modalElement) {
    // Remove body scroll lock
    document.body.classList.remove('fresh-modal-open');
    
    // Hide modal
    modalElement.classList.remove('fresh-modal--active');
}

/**
 * Initialize modal close handlers
 * Handles close button clicks, overlay clicks, and ESC key
 */
function freshInitModalHandlers() {
    // Close button clicks
    document.addEventListener('click', function(event) {
        const closeButton = event.target.closest('.fresh-modal__close');
        if (closeButton) {
            event.preventDefault();
            const modal = closeButton.closest('.fresh-modal');
            if (modal) {
                freshCloseModal(modal);
            }
        }
    });
    
    // Overlay clicks (close modal when clicking outside content)
    document.addEventListener('click', function(event) {
        const modal = event.target.closest('.fresh-modal');
        if (modal && event.target.classList.contains('fresh-modal__overlay')) {
            freshCloseModal(modal);
        }
    });
    
    // ESC key to close modal
    document.addEventListener('keydown', function(event) {
        if (event.key === 'Escape' || event.keyCode === 27) {
            const activeModal = document.querySelector('.fresh-modal.fresh-modal--active');
            if (activeModal) {
                freshCloseModal(activeModal);
            }
        }
    });
}

/**
 * Auto-wrap elements with fresh-modal-content class into modal structure
 * This allows users to create modals by simply adding the class without
 * manually writing the modal HTML structure.
 */
function freshInitModalAutoWrap() {
    // Find all elements that should become modals
    const modalContents = document.querySelectorAll('.fresh-modal-content');
    
    modalContents.forEach(function(contentElement) {
        // Skip if already wrapped
        if (contentElement.closest('.fresh-modal')) {
            return;
        }
        
        // Get the ID from the element (or generate one)
        let modalId = contentElement.id;
        
        // If no ID attribute, check classes for custom ID or auto-generated ID
        if (!modalId) {
            const classes = Array.from(contentElement.classList);
            
            // The snippet puts the custom ID as the FIRST class if set
            // Auto-generated IDs follow pattern: freshliquid--123456789 or freshHTML--123456789
            // Custom IDs are user-friendly names like "west-la-section"
            const firstClass = classes[0];
            
            // Check if first class matches auto-generated pattern
            // Pattern: starts with "freshliquid--" or "freshHTML--" followed by numbers
            const isAutoGeneratedPattern = firstClass && (
                /^freshliquid--\d+$/.test(firstClass) || 
                /^freshHTML--\d+$/.test(firstClass)
            );
            
            if (firstClass && !isAutoGeneratedPattern) {
                // First class is a custom ID (e.g., "west-la-section")
                modalId = firstClass;
            } else {
                // Try to find auto-generated ID in classes
                const idClass = classes.find(function(c) {
                    return /^freshliquid--\d+$/.test(c) || /^freshHTML--\d+$/.test(c);
                });
                
                if (idClass) {
                    // Convert class to ID format (e.g., freshliquid--123 -> freshliquid-123)
                    modalId = idClass.replace(/--/g, '-');
                } else {
                    // Generate unique ID as fallback
                    modalId = 'fresh-modal-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
                }
            }
            
            // Set the ID attribute for future reference
            contentElement.id = modalId;
        }
        
        // Create modal wrapper
        const modalWrapper = document.createElement('div');
        modalWrapper.className = 'fresh-modal';
        modalWrapper.id = modalId;
        
        // Create overlay
        const overlay = document.createElement('div');
        overlay.className = 'fresh-modal__overlay';
        
        // Create content wrapper
        const contentWrapper = document.createElement('div');
        contentWrapper.className = 'fresh-modal__content';
        
        // Create close button
        const closeButton = document.createElement('button');
        closeButton.className = 'fresh-modal__close';
        closeButton.setAttribute('aria-label', 'Close modal');
        
        // Move original content into content wrapper
        // Use DocumentFragment for better performance
        const fragment = document.createDocumentFragment();
        while (contentElement.firstChild) {
            fragment.appendChild(contentElement.firstChild);
        }
        contentWrapper.appendChild(fragment);
        
        // Insert close button at the beginning
        contentWrapper.insertBefore(closeButton, contentWrapper.firstChild);
        
        // Assemble structure
        modalWrapper.appendChild(overlay);
        modalWrapper.appendChild(contentWrapper);
        
        // Replace original element with modal wrapper
        contentElement.parentNode.replaceChild(modalWrapper, contentElement);
        
        // Log for debugging
        if (window.Freshwater && window.Freshwater.console) {
            window.Freshwater.console('log', 'Freshwater Modal: Auto-wrapped element with ID "' + modalId + '"');
        }
    });
}

// Initialize modal handlers on page load
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
        freshInitModalHandlers();
        freshInitModalAutoWrap();
    });
} else {
    freshInitModalHandlers();
    freshInitModalAutoWrap();
}

// Re-run auto-wrap after Shopify section loads (for theme editor)
document.addEventListener('shopify:section:load', freshInitModalAutoWrap);

/**
 * On the fly add functionality buttons.
 * Finds all .fresh-button elements and adds functionality based on class names:
 * - freshvariant-{id}: Adds product variant to cart
 * - freshquantity-{qty}: Sets quantity for cart add (default: 1)
 * - freshsellingplan-{id}: Sets selling plan ID for subscriptions
 * - freshklaviyo-{id}: Opens Klaviyo sign up form
 * - freshscroll-{id}: Smooth scrolls to element with matching ID
 * - fresh-modal-button: Opens modal by ID from href (e.g., #my-section)
 * 
 * Uses event delegation for better performance and compatibility with dynamic content.
 * Uses vanilla JavaScript for better performance and no jQuery dependency.
 * 
 * @author Alexander Khost
 */
function freshAddFunctionalityButtons() {
    // Use event delegation on document for better performance and dynamic content support
    document.addEventListener('click', function(event) {
        const button = event.target.closest('.fresh-button');
        
        // Skip if not a fresh-button
        if (!button) {
            return;
        }
        
        // Get all classes from the button
        const classList = button.classList;
        const classes = Array.from(classList);
        
        // Initialize variables
        let variantId = null;
        let quantity = 1;
        let sellingPlanId = null;
        let klaviyoFormId = null;
        let scrollId = null;
        
        // Parse classes to extract functionality
        classes.forEach(function(className) {
            // Extract variant ID
            const variantValue = freshExtractClassValue(className, 'freshvariant');
            if (variantValue) {
                variantId = variantValue;
            }
            
            // Extract quantity
            const quantityValue = freshExtractClassValue(className, 'freshquantity');
            if (quantityValue) {
                const parsedQty = parseInt(quantityValue, 10);
                if (!isNaN(parsedQty) && parsedQty > 0) {
                    quantity = parsedQty;
                }
            }
            
            // Extract selling plan ID
            const sellingPlanValue = freshExtractClassValue(className, 'freshsellingplan');
            if (sellingPlanValue) {
                sellingPlanId = sellingPlanValue;
            }
            
            // Extract Klaviyo form ID
            const klaviyoValue = freshExtractClassValue(className, 'freshklaviyo');
            if (klaviyoValue) {
                klaviyoFormId = klaviyoValue;
            }
            
            // Extract scroll ID
            const scrollValue = freshExtractClassValue(className, 'freshscroll');
            if (scrollValue) {
                scrollId = scrollValue;
            }
        });
        
        // Handle Klaviyo form (highest priority - executes immediately)
        if (klaviyoFormId) {
            event.preventDefault();
            event.stopPropagation();
            
            // Initialize Klaviyo if not already initialized
            window._klOnsite = window._klOnsite || [];
            window._klOnsite.push(['openForm', klaviyoFormId]);
            
            return false;
        }
        
        // Check if button has fresh-modal-button class
        if (button.classList.contains('fresh-modal-button')) {
            const handled = freshHandleModal(button);
            if (handled) {
                event.preventDefault();
                event.stopPropagation();
                return false;
            }
        }
        
        // Handle smooth scroll
        if (scrollId) {
            event.preventDefault();
            event.stopPropagation();
            
            const targetElement = document.getElementById(scrollId);
            if (targetElement) {
                // Use the existing smooth scroll function
                const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                const scrollDuration = prefersReducedMotion ? 0 : 500;
                
                // Reuse smooth scroll logic from freshSmoothScrollToInlineAnchors
                if (scrollDuration <= 0) {
                    targetElement.scrollIntoView({ behavior: 'auto', block: 'start' });
                } else {
                    const startPosition = window.pageYOffset || document.documentElement.scrollTop;
                    const targetPosition = targetElement.getBoundingClientRect().top + startPosition;
                    const distance = targetPosition - startPosition;
                    let startTime = null;
                    
                    function animation(currentTime) {
                        if (startTime === null) {
                            startTime = currentTime;
                        }
                        
                        const timeElapsed = currentTime - startTime;
                        const progress = Math.min(timeElapsed / scrollDuration, 1);
                        
                        // Easing function (ease-in-out)
                        const ease = progress < 0.5 
                            ? 2 * progress * progress 
                            : 1 - Math.pow(-2 * progress + 2, 2) / 2;
                        
                        window.scrollTo(0, startPosition + distance * ease);
                        
                        if (timeElapsed < scrollDuration) {
                            requestAnimationFrame(animation);
                        } else {
                            window.scrollTo(0, targetPosition);
                        }
                    }
                    
                    requestAnimationFrame(animation);
                }
            } else {
                window.Freshwater.console('warn', 'Scroll target not found:', scrollId);
            }
            
            return false;
        }
        
        // Handle add to cart (lowest priority - executes after other handlers)
        if (variantId) {
            event.preventDefault();
            event.stopPropagation();
            
            freshAddProductToCart(variantId, quantity, sellingPlanId)
                .catch(function(error) {
                    window.Freshwater.console('error', 'Failed to add to cart:', error);
                });
            
            return false;
        }
    });
}

/**
 * Initializes mega menu submenu behavior.
 * Handles hover interactions for desktop mega menus and keeps 3rd-level dropdowns open.
 * Uses event delegation for compatibility with dynamically loaded content.
 */
const freshInitMegaMenu = function() {
    // Keep 3rd-level dropdown submenus open by default
    document.querySelectorAll('header-menu .header__submenu details').forEach(function(submenu) {
        submenu.setAttribute('open', '');
    });

    // Desktop-only hover behavior – ONLY for mega menus
    if (!window.matchMedia('(pointer: fine)').matches) return;

    var stickyHeader = document.querySelector('sticky-header');
    if (!stickyHeader) return;

    /**
     * Gets all mega menus (cached query for performance).
     * Re-queries to handle dynamically added menus.
     * Note: Internal functions don't use 'fresh' prefix as they're scoped to this function.
     * @returns {NodeList} All mega menu elements
     */
    function getAllMegaMenus() {
        return document.querySelectorAll('header-menu details.mega-menu');
    }

    /**
     * Opens the target mega menu and closes all others.
     * @param {Element} targetMenu - The mega menu element to open
     */
    function openMegaMenu(targetMenu) {
        var megaMenus = getAllMegaMenus();
        var i = megaMenus.length;
        while (i--) {
            var menu = megaMenus[i];
            if (menu === targetMenu) {
                menu.setAttribute('open', '');
            } else {
                menu.removeAttribute('open');
            }
        }
    }

    /**
     * Closes all mega menus.
     */
    function closeAllMegaMenus() {
        var megaMenus = getAllMegaMenus();
        var i = megaMenus.length;
        while (i--) {
            megaMenus[i].removeAttribute('open');
        }
    }

    // Performance: Cache sticky header reference, but check dynamically for section reloads
    // Use a single combined event handler for better performance
    document.addEventListener('mouseover', function(event) {
        var target = event.target;
        var relatedTarget = event.relatedTarget;
        
        // Early exit: Only process events within sticky header
        // Query once per event and cache in closure for this handler
        var currentStickyHeader = document.querySelector('sticky-header');
        if (!currentStickyHeader || !currentStickyHeader.contains(target)) {
            return;
        }
        
        // Handle mega menu hover
        var megaMenu = target.closest('header-menu details.mega-menu');
        if (megaMenu && (!relatedTarget || !megaMenu.contains(relatedTarget))) {
            var summary = megaMenu.querySelector(':scope > summary');
            var submenu = megaMenu.querySelector(':scope > .mega-menu__content');
            
            // Check if hover is on summary or submenu
            if (summary && (summary === target || summary.contains(target))) {
                openMegaMenu(megaMenu);
                return; // Early exit after handling
            } else if (submenu && (submenu === target || submenu.contains(target))) {
                openMegaMenu(megaMenu);
                return; // Early exit after handling
            }
        }
        
        // Handle top-level links without submenu
        var link = target.closest('.header__inline-menu > ul > li > a.header__menu-item');
        if (link && (!relatedTarget || !link.contains(relatedTarget))) {
            var parentMegaMenu = link.closest('header-menu details.mega-menu');
            if (!parentMegaMenu) {
                closeAllMegaMenus();
            }
        }
    }, true); // Use capture phase for better performance

    // Close all mega menus when mouse leaves the sticky header
    // Performance: Only check if menus are actually open before closing
    document.addEventListener('mousemove', function(event) {
        var currentStickyHeader = document.querySelector('sticky-header');
        if (currentStickyHeader && !currentStickyHeader.contains(event.target)) {
            // Only close if there are actually open menus (performance optimization)
            var openMenus = currentStickyHeader.querySelectorAll('header-menu details.mega-menu[open]');
            if (openMenus.length > 0) {
                closeAllMegaMenus();
            }
        }
    });
};

// ============================================================================
// EVENT LISTENERS
// ============================================================================
// CAROUSEL HELPER FUNCTIONS
// ============================================================================

/**
 * Check if carousel controls should be hidden based on slide count.
 * Adds/removes 'fresh-no-scroll-needed' class on the section element.
 * 
 * @param {string} sectionId - The ID of the section element (e.g., '#freshSection--...')
 * @param {number} itemsToShow - Number of items/slides to show at once
 * @returns {void}
 */
function freshCheckCarouselControls(sectionId, itemsToShow) {
    var section = document.querySelector(sectionId);
    if (!section) return;

    var carousel = section.querySelector('.fresh-carousel[data-slider-config]');
    if (!carousel) return;

    // Count original slides (exclude cloned slides)
    var originalSlides = carousel.querySelectorAll('li:not(.tns-cloned)').length;

    // Hide controls if slides <= items to show
    if (originalSlides <= itemsToShow) {
        section.classList.add('fresh-no-scroll-needed');
    } else {
        section.classList.remove('fresh-no-scroll-needed');
    }
}

// ============================================================================

// Performance: Combine DOMContentLoaded listeners into one handler
document.addEventListener('DOMContentLoaded', function(e) {
    freshGlobalInit(e);
    freshInitMegaMenu();
}, false);

// Window resize: Debounced resize handler for carousel reinitialization
window.addEventListener('resize', function(e) {
    if (freshWindowResizeTimeout) {
        clearTimeout(freshWindowResizeTimeout);
    }
    freshWindowResizeTimeout = setTimeout(function() {
        freshGlobalResize(e);
    }, 500);
}, false);